import { type LinearConfiguratorState } from "~/features/configurator/Linear/redux/types";
import {
  CUSTOM_SEGMENT_LENGTH_SELECTED,
  CUSTOM_SEGMENT_LENGTH_SELECTED_RESET,
  HIDE_LOADER,
  LOAD_DATA,
  MARK_ALREADY_ADDED_TO_CART,
  POST_HYBRIS_UPDATE,
  RESET_CURRENT_STEP,
  RESTORE_SNAPSHOT,
  SET_STEP,
  SET_TAB,
  type setLCSummaryTabAction,
  SHOW_LOADER,
  TOGGLE_LINEAR_CARRIAGE_EDIT,
  UPDATE_ADDITIONAL_DATA,
  UPDATE_CSTICS,
  UPDATE_CSTICS_FROM_TILE,
  UPDATE_PRICE_REFERENCE,
  UPDATE_QUANTITY,
  UPDATE_SMDS_FLIP,
  VALIDATE_CURRENT_STEP,
} from "~/features/configurator/Linear/redux/actions";
import { type AnyAction } from "~/app/store/types";
import {
  type Kmat,
  type LinearConfiguration,
  type LinearSubstep,
  SimpleCsticId,
  type SubstepTile,
} from "~/features/configurator/Linear/models";
import { SubstepType } from "~/features/configurator/Linear/dtos";
import {
  constructCsticToKmatTypeMap,
  csticUpdatesFromDict,
  findSelectedTileIndex,
  fromExtraValidationToDirtyParamName,
  getCsticNumberVal,
  getCsticVal,
} from "~/features/configurator/Linear/utils";
import { type CsticUpdate } from "~/features/configurator/Linear/api/sendLinearConfiguratorUpdate";
import {
  CSTIC_AL_NAME,
  CSTIC_AR_NAME,
  CSTIC_LENGTH_NAME,
  CSTIC_PART_MAX_SIZE_NAME,
  CSTIC_PART_MIN_SIZE_NAME,
  CSTIC_SCHMIERANSCHLUSS_NAME,
  CSTIC_SCHMIERANSCHLUSS_POSITION_NAME,
  CSTIC_VSKITS_NAME,
  FAKE_CSTIC_UNIT_LENGTH,
  GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME,
  getEditCarriageCsticsToReset,
  isGuidewayPieceSizeCsticName,
  UNMOUNTED_LEFT,
  UNMOUNTED_RIGHT,
} from "~/features/configurator/Linear/constants";
import { ConfiguratorType } from "~/domains/configurator/types/ConfiguratorType";

import { type Draft, produce } from "immer";
import {
  EXTRA_DATA_DIRTY_PARAM_PREFIX,
  getDirtyParamName,
  getExtraValidationParamName,
} from "~/features/configurator/Linear/namingUtils";
import { LcSummaryTab } from "~/features/configurator/common/LcSummaryTab";
import { getMainKmat } from "~/features/configurator/Shaft/common/commonShaftHelpers";
import {
  CARRIAGE_CONFIGURATOR_PRODUCT_CODE,
  INCH_SHAFT_CONFIGURATOR_PRODUCT_CODE,
  KLLT_UNIT_CONFIGURATOR_PRODUCT_CODE,
  KWLT_CARRIAGE_CONFIGURATOR_PRODUCT_CODE,
  METRIC_SHAFT_CONFIGURATOR_PRODUCT_CODE,
  RAIL_CONFIGURATOR_PRODUCT_CODE,
  TKDM_RAIL_CONFIGURATOR_PRODUCT_CODE,
  TKLT_RAIL_CONFIGURATOR_PRODUCT_CODE,
} from "~/domains/configurator/constants";
import { AlarHardMax } from "~/features/configurator/Rail/v2/configuration/classic/RailsCstics";
import { getConflictCsticNames, validateFromDefinition } from "~/features/configurator/common/v2/validation";
import { selectDeepCarriageKmatType, selectDeepRailKmatType } from "~/features/configurator/Linear/redux/selectors";
import { KmatType } from "~/features/configurator/Linear/kmatType";

const EMPTY_SUBSTEP: LinearSubstep = {
  kmatType: KmatType.Main,
  cstics: {
    helper: {},
    input: {},
  },
  groupedTiles: [],
  tiles: [],
  selectedTile: null,
  additionalValidations: {},
  additionalData: {},
  description: "",
  info: "",
  name: "",
  tilesPerRow: 0,
  type: SubstepType.SummaryPage,
  value: null,
  footerInfo: [],
  extraTexts: {},
  isVisible: true,
  extraConfig: {},
};

export const initialState: LinearConfiguratorState = {
  currentStep: 0, //change after creating summary
  highTideStep: 0,
  activeTab: LcSummaryTab.ProductInformation,
  kmats: {
    [KmatType.Main]: [],
    [KmatType.SubCarriage]: [],
    [KmatType.SubRail]: [],
  },
  steps: [],
  substeps: {},
  systemLayoutHelpTile: null,

  groupIdToDisplay: "",
  productCode: "",
  configId: "",

  minLength: null,
  maxLength: null,
  allowedSteps: [[], []],
  showLoader: false,

  conflicts: [],

  designation: "",
  schaefflerId: "",
  description: "",

  guidewayPieceLimits: {},
  quantity: 1,
  isConfigComplete: false,
  manufacturerLogo: null,
  step5PdfLink: null,

  helpUrl: "",
  productImageUrl: "",
  specialTranslations: {},
  advancedSpecialTranslations: {},
  resetCarriageDetails: true,
  enableConfigureCarriageValues: true,
  customSegmentLengthSelected: false,
  customSecondSegmentLengthSelected: false,
  cadUrl: "",
  cadXmlRequest: "",
  addedToCart: false,
  priceReference: "",
  enableCarriageEdit: [],
  kbInfo: [],
};

function shouldUseNewValidation(draft: Draft<LinearConfiguratorState>) {
  const productCodes = [
    METRIC_SHAFT_CONFIGURATOR_PRODUCT_CODE,
    INCH_SHAFT_CONFIGURATOR_PRODUCT_CODE,
    RAIL_CONFIGURATOR_PRODUCT_CODE,
    TKDM_RAIL_CONFIGURATOR_PRODUCT_CODE,
    TKLT_RAIL_CONFIGURATOR_PRODUCT_CODE,
    CARRIAGE_CONFIGURATOR_PRODUCT_CODE,
    KWLT_CARRIAGE_CONFIGURATOR_PRODUCT_CODE,
    KLLT_UNIT_CONFIGURATOR_PRODUCT_CODE,
  ];
  return productCodes.includes(draft.productCode);
}

function validateStep(draft: Draft<LinearConfiguratorState>, substepType: SubstepType) {
  const substep = draft.substeps[substepType];

  if (shouldUseNewValidation(draft)) {
    const { substepValid, extraValidations, additionalData } = validateFromDefinition(
      substep,
      draft.kmats,
      draft.conflicts,
    );
    substep.isInvalid = !substepValid;
    substep.isLenientInvalid = !substepValid;
    Object.entries(extraValidations).forEach(([key, value]) => {
      substep.additionalValidations[key] = value;
    });
    Object.entries(additionalData).forEach(([key, value]) => {
      substep.additionalData[key] = value;
    });
  } else {
    const validator = VALIDATORS[substepType] || DEFAULT_VALIDATION;
    const validationResult = validator(draft, substep);
    const isInvalid = !validationResult.substepValid;
    const validationResultDirtyKeys = Object.keys(validationResult.extraValidations).map(
      fromExtraValidationToDirtyParamName,
    );
    substep.isInvalid = isInvalid;
    substep.isLenientInvalid = isInvalid;
    validationResultDirtyKeys.forEach((key) => {
      substep.additionalData[key] = true;
    });
    Object.entries(validationResult.extraValidations).forEach(([key, value]) => {
      substep.additionalValidations[key] = value;
    });
  }
}

export const linearConfiguratorReducer = (
  state: LinearConfiguratorState = initialState,
  action: AnyAction,
): LinearConfiguratorState => {
  switch (action.type) {
    case SHOW_LOADER: {
      return {
        ...state,
        showLoader: true,
      };
    }
    case HIDE_LOADER: {
      return {
        ...state,
        showLoader: false,
      };
    }

    case LOAD_DATA: {
      const { data, helpUrl, type } = action.payload as {
        data: LinearConfiguration;
        helpUrl: string;
        type: ConfiguratorType;
      };
      const { substeps, kmats: kmatMap, steps, ...rest } = data;

      const currentStep = rest.currentStep - 1;
      const highestVisitedStep = rest.highestVisitedStep - 1;

      Object.values(substeps).forEach((substep) => {
        substep.selectedTile = findSelectedTileIndex(substep, kmatMap);
      });

      steps.forEach((step, index) => (step.active = rest.allowedSteps[highestVisitedStep][index]));

      const newState = {
        ...state,
        ...rest,
        substeps,
        steps,
        kmats: kmatMap,
        currentStep: currentStep,
        highTideStep: highestVisitedStep,
        helpUrl,
        type,
        productImageUrl: data.mediaUrl,
      };
      activateRailsSummaryAfterStep4LengthFilled(newState);
      return {
        ...newState,
        previousState: { ...newState, previousState: undefined },
      };
    }

    case POST_HYBRIS_UPDATE: {
      const {
        hybrisData: {
          kmats: kmatMap,
          steps: updatedSteps,
          substeps: updatedSubsteps,
          conflictedCstics,
          minLength: minLengthValue,
          maxLength: maxLengthValue,
          mediaUrl,
          currentStep: currentStepValue,
          highestVisitedStep: highestVisitedStepValue,
          ...rest
        },
        navigationUpdate,
      } = action.payload as {
        hybrisData: LinearConfiguration;
        navigationUpdate?: boolean;
      };

      const newState = produce(state, (draft) => {
        draft.steps.forEach((originalStep, index) => {
          const stepUpdate = updatedSteps[index];
          if (stepUpdate?.defaultValues) {
            originalStep.defaultValues = {
              ...originalStep.defaultValues,
              ...stepUpdate.defaultValues,
            };
          }
        });

        draft.highTideStep = navigationUpdate ? state.highTideStep : state.currentStep;
        if (rest.allowedSteps) {
          draft.allowedSteps = rest.allowedSteps;
        }
        if (rest.nextSteps) {
          draft.nextSteps = rest.nextSteps;
        }
        if (rest.previousSteps) {
          draft.previousSteps = rest.previousSteps;
        }
        if (!navigationUpdate) {
          draft.steps.forEach((step, index) => (step.active = draft.allowedSteps![draft.highTideStep][index]));
        }

        // Updated substeps only contains dynamic substeps, in that case we trust BE to provide available tiles each time
        Object.values(updatedSubsteps).forEach((newSubstep) => {
          const oldSubstep = draft.substeps[newSubstep.type];
          const updatedSubstep: LinearSubstep = {
            ...(oldSubstep || EMPTY_SUBSTEP),
            ...newSubstep,
            additionalData: {
              ...(oldSubstep || EMPTY_SUBSTEP).additionalData,
              ...newSubstep.additionalData,
            },
          };
          draft.substeps[updatedSubstep.type] = updatedSubstep;
        });

        Object.values(draft.substeps).forEach((substep) => {
          substep.selectedTile = findSelectedTileIndex(substep, kmatMap);
        });

        const substeps = draft.substeps;

        draft.kmats = kmatMap;
        draft.conflicts = conflictedCstics;
        draft.minLength = minLengthValue;
        draft.maxLength = maxLengthValue;
        draft.showLoader = false;
        draft.previousState = conflictedCstics.length > 0 ? draft.previousState : undefined;
        draft.productImageUrl = mediaUrl;

        Object.assign(draft, rest);

        if (substeps[SubstepType.GuidewaySet]) {
          const extraParams = updateGuidewaySetValidations(draft, true);
          const substep = draft.substeps[SubstepType.GuidewaySet];
          Object.assign(substep.additionalValidations, extraParams);

          const isInvalid = Object.values(substep.additionalValidations).some((valid) => !valid);
          const isLenientInvalid = Object.entries(substep.additionalValidations).some(([key, valid]) => {
            if (isGuidewayPieceSizeCsticName(key) || key.includes(CSTIC_LENGTH_NAME)) {
              const isDirty = substep.additionalData[fromExtraValidationToDirtyParamName(key)] ?? false;
              return !valid && isDirty;
            }
            return !valid;
          });
          substep.isInvalid = isInvalid;
          substep.isLenientInvalid = isLenientInvalid;
        }

        if (substeps[SubstepType.CarriageDetails]) {
          const substep = substeps[SubstepType.CarriageDetails];
          const validationResult = VALIDATORS[SubstepType.CarriageDetails](draft, substep);
          Object.assign(substep.additionalValidations, validationResult.extraValidations);
          substep.isInvalid = !validationResult.substepValid;
          substep.isLenientInvalid = !validationResult.substepValid;
        }

        activateRailsSummaryAfterStep4LengthFilled(draft);

        if (!navigationUpdate && shouldUseNewValidation(draft) && draft.conflicts.length > 0) {
          // Update validation state from conflicts
          const cstics: string[] = getConflictCsticNames(draft.conflicts);

          draft.steps[draft.currentStep].substeps
            .map((substepType) => draft.substeps[substepType])
            .forEach((substep) => validateStepFromConflicts(substep, cstics));
        }

        draft.steps[draft.currentStep].substeps
          .map((substepType) => draft.substeps[substepType])
          .filter((substep) => substep.isInvalid || substep.isLenientInvalid)
          .forEach((substep) => validateStep(draft, substep.type));
      });

      return newState;
    }

    case UPDATE_CSTICS: {
      const { updates, owningSubstep } = action.payload as {
        updates: CsticUpdate[];
        owningSubstep: SubstepType;
      };

      const kmats = constructUpdatedKmats(state.kmats, updates);
      const substeps = { ...state.substeps };

      const newState = {
        ...state,
        addedToCart: false,
        previousState: state.previousState ? state.previousState : { ...state, previousState: undefined },
        kmats,
        substeps,
      };

      if (owningSubstep === SubstepType.GuidewaySet) {
        const extraParams = updateGuidewaySetValidations(newState, true);
        const substep = { ...state.substeps[SubstepType.GuidewaySet] };
        substeps[SubstepType.GuidewaySet] = substep;
        substep.additionalValidations = {
          ...substep.additionalValidations,
          ...extraParams,
        };
        const isInvalid = Object.values(substep.additionalValidations).some((valid) => !valid);
        const isLenientInvalid = Object.entries(substep.additionalValidations).some(([key, valid]) => {
          if (isGuidewayPieceSizeCsticName(key) || key.includes(CSTIC_LENGTH_NAME)) {
            const isDirty = substep.additionalData[fromExtraValidationToDirtyParamName(key)] ?? false;
            return !valid && isDirty;
          }
          return !valid;
        });
        substep.isInvalid = isInvalid;
        substep.isLenientInvalid = isLenientInvalid;
        newState.substeps = substeps;
      }

      if (owningSubstep === SubstepType.CarriageDetails) {
        const substep = substeps[SubstepType.CarriageDetails];
        const validationResult = VALIDATORS[SubstepType.CarriageDetails](newState, substep);
        substeps[SubstepType.CarriageDetails] = {
          ...substep,
          additionalValidations: {
            ...substep.additionalValidations,
            ...validationResult.extraValidations,
          },
          isInvalid: !validationResult.substepValid,
          isLenientInvalid: !validationResult.substepValid,
        };
      }
      return newState;
    }

    case UPDATE_ADDITIONAL_DATA: {
      const { updates, owningSubstep } = action.payload as {
        updates: CsticUpdate[];
        owningSubstep: SubstepType;
      };

      return produce(state, (draft) => {
        const substep = draft.substeps[owningSubstep]!;
        updates.forEach((update) => {
          const key = getDirtyParamName(update);
          substep.additionalData[key] = true;
        });
        const isAllDirty = Object.entries(substep.additionalData)
          .filter(([key]) => key.startsWith(EXTRA_DATA_DIRTY_PARAM_PREFIX))
          .every(([_, value]) => value);
        if (isAllDirty) {
          substep.isLenientInvalid = substep.isInvalid;
        }
        draft.addedToCart = false;
      });
    }
    case UPDATE_SMDS_FLIP: {
      const flip = action.payload as boolean;

      return {
        ...state,
        addedToCart: false,
        substeps: {
          ...state.substeps,
          [SubstepType.SMDS]: {
            ...state.substeps[SubstepType.SMDS],
            value: flip,
          },
        },
      };
    }

    case CUSTOM_SEGMENT_LENGTH_SELECTED: {
      const { value, kmatIndex } = action.payload as { value: boolean; kmatIndex: number };

      return {
        ...state,
        addedToCart: false,
        previousState: state.previousState ? state.previousState : { ...state, previousState: undefined },
        customSegmentLengthSelected: kmatIndex === 0 ? value : state.customSegmentLengthSelected,
        customSecondSegmentLengthSelected: kmatIndex === 1 ? value : state.customSecondSegmentLengthSelected,
      };
    }

    case CUSTOM_SEGMENT_LENGTH_SELECTED_RESET: {
      return {
        ...state,
        addedToCart: false,
        previousState: state.previousState ? state.previousState : { ...state, previousState: undefined },
        customSegmentLengthSelected: false,
        customSecondSegmentLengthSelected: false,
      };
    }

    case UPDATE_CSTICS_FROM_TILE: {
      const { elementType, tileIndex, tile } = action.payload as {
        elementType: SubstepType;
        tileIndex: number;
        tile: SubstepTile;
      };

      const substeps: Dict<LinearSubstep> = {
        ...state.substeps,
        [elementType]: {
          ...state.substeps[elementType],
          selectedTile: tileIndex,
          isInvalid: false,
          isLenientInvalid: false,
        },
      };
      const kmats = constructUpdatedKmats(
        state.kmats,
        csticUpdatesFromDict(tile.cstics, constructCsticToKmatTypeMap(state.kmats)),
      );

      return {
        ...state,
        addedToCart: false,
        previousState: state.previousState ? state.previousState : { ...state, previousState: undefined },
        kmats,
        substeps,
      };
    }

    case RESET_CURRENT_STEP: {
      const stepData = state.steps[state.currentStep];

      const kmats = constructUpdatedKmats(
        state.kmats,
        csticUpdatesFromDict(stepData.defaultValues, constructCsticToKmatTypeMap(state.kmats)),
      );

      const substeps = { ...state.substeps };
      stepData.substeps.forEach((elementType) => {
        substeps[elementType] = {
          ...substeps[elementType],
          selectedTile: findSelectedTileIndex(substeps[elementType], kmats),
          isInvalid: false,
          isLenientInvalid: false,
        };
      });

      let newState: LinearConfiguratorState = {
        ...state,
        previousState: undefined,
        addedToCart: false,
        kmats,
        substeps,
      };

      stepData.substeps.forEach((elementType) => {
        const specialBehavior = SPECIAL_RESET_BEHAVIOR[elementType];
        if (specialBehavior) {
          newState = specialBehavior(newState);
        }
      });

      return newState;
    }

    case VALIDATE_CURRENT_STEP: {
      return produce(state, (draft) => {
        draft.steps[state.currentStep].substeps.forEach((substep) => validateStep(draft, substep));
      });
    }

    case SET_STEP: {
      const index = action.payload as number;

      const highTideStep = index > state.highTideStep ? index : state.highTideStep;

      return {
        ...state,
        currentStep: index,
        highTideStep,
      };
    }

    case SET_TAB: {
      const a = action as ReturnType<typeof setLCSummaryTabAction>;
      const tab = a.payload;

      if (tab === state.activeTab) {
        return state;
      }

      return {
        ...state,
        activeTab: tab,
      };
    }

    case RESTORE_SNAPSHOT: {
      return state.previousState ? { ...state.previousState } : state;
    }

    case UPDATE_QUANTITY: {
      const newQ = action.payload as number;
      return {
        ...state,
        quantity: newQ,
      };
    }

    case UPDATE_PRICE_REFERENCE: {
      const newPR = action.payload as string;
      return {
        ...state,
        priceReference: newPR,
        addedToCart: false,
      };
    }

    case MARK_ALREADY_ADDED_TO_CART: {
      const newConfigId = action.payload;
      if (newConfigId) {
        return {
          ...state,
          configId: newConfigId,
        };
      } else {
        return {
          ...state,
          addedToCart: true,
        };
      }
    }

    case TOGGLE_LINEAR_CARRIAGE_EDIT: {
      const { carriageIndex, statusEdit } = action.payload as {
        carriageIndex: number;
        statusEdit: boolean;
      };

      const temp = state.enableCarriageEdit?.map((item, index) => {
        if (carriageIndex === undefined || index === carriageIndex) {
          return statusEdit;
        }

        return item;
      });

      return {
        ...state,
        addedToCart: false,
        enableCarriageEdit: temp,
      };
    }

    default: {
      return state;
    }
  }
};

type ValidatorFunc = (
  state: LinearConfiguratorState,
  substep: LinearSubstep,
) => {
  substepValid: boolean;
  extraValidations: Dict<boolean>;
};

const DEFAULT_VALIDATION: ValidatorFunc = (state, substep) => ({
  substepValid: substep.selectedTile !== null || substep.value !== null,
  extraValidations: {},
});

const VALIDATORS: Dict<ValidatorFunc> = {
  [SubstepType.LocatingFace]: () => ({
    // There are always some values selected in Locating Face
    substepValid: true,
    extraValidations: {},
  }),
  [SubstepType.CarriageDetails]: (state, substep) => {
    const validations = updateCarriageValidations(state, false);
    return {
      substepValid: Object.values(validations).every((valid) => valid),
      extraValidations: validations,
    };
  },
  [SubstepType.SummaryPage]: () => ({
    substepValid: true,
    extraValidations: {},
  }),
  [SubstepType.Length]: (state, substep) => {
    const mainKmat = getMainKmat({ kmats: state.kmats });
    const lengthCstic = mainKmat.cstics[FAKE_CSTIC_UNIT_LENGTH];
    const length = lengthCstic.value ? lengthCstic.value : "0";
    const isValid = isLengthValid(state.minLength, state.maxLength, Number.parseFloat(length));
    return {
      substepValid: isValid,
      extraValidations: {
        [getExtraValidationParamName(SimpleCsticId(FAKE_CSTIC_UNIT_LENGTH))]: isValid,
      },
    };
  },
  [SubstepType.GuidewaySet]: (state, substep) => {
    const validations = updateGuidewaySetValidations(state, false);
    const validationsOk = Object.values(validations).every((valid) => valid);

    const hasLengthSuggestions = state.kmats[KmatType.SubRail].some(
      (kmat) =>
        Object.entries(kmat.cstics).filter(
          ([csticName, _]) => csticName.endsWith("_U_64") || csticName.endsWith("_O_64"),
        ).length > 0,
    );

    return {
      substepValid: validationsOk && !hasLengthSuggestions,
      extraValidations: validations,
    };
  },
  [SubstepType.ReferenceCarriage]: () => ({
    // Reference is always true, because it sometimes may have no value
    substepValid: true,
    extraValidations: {},
  }),
  [SubstepType.SMDS]: (state, substep) => ({
    // Valid if not visible
    substepValid: substep?.isVisible ? DEFAULT_VALIDATION(state, substep).substepValid : true,
    extraValidations: {},
  }),
  [SubstepType.SMDE]: (state, substep) => ({
    // Valid if not visible
    substepValid: substep?.isVisible ? DEFAULT_VALIDATION(state, substep).substepValid : true,
    extraValidations: {},
  }),
};

const SPECIAL_RESET_BEHAVIOR = {
  [SubstepType.Length]: (state: LinearConfiguratorState) => {
    const mainKmat = getMainKmat({ kmats: state.kmats });
    const lengthCstic = mainKmat.cstics[FAKE_CSTIC_UNIT_LENGTH];
    if (lengthCstic) {
      lengthCstic.value = undefined;
    }

    return state;
  },
};

const updateGuidewaySetValidations = (state: LinearConfiguratorState, ignoreEmpty: boolean) => {
  const kmatType = selectDeepRailKmatType(state);
  const extraParameters: Dict<boolean> = {};
  state.kmats[kmatType]?.forEach((kmat, kmatIndex) => {
    // Validate Length
    let validationObj = {
      index: kmatIndex,
      type: kmatType,
      key: CSTIC_LENGTH_NAME,
      value: getCsticVal(kmat.cstics[CSTIC_LENGTH_NAME]),
    };
    let valid = GUIDEWAY_LENGTH_VALIDATOR(state, validationObj, ignoreEmpty);
    const extraParamName = getExtraValidationParamName(validationObj);
    extraParameters[extraParamName] = valid;

    // Validate all partial lengths
    let index = 1;

    while (true) {
      const csticKey = GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME(index);

      const cstic = kmat.cstics[csticKey];
      if (!cstic) {
        break;
      }

      validationObj = {
        index: kmatIndex,
        type: kmatType,
        key: csticKey,
        value: getCsticVal(kmat.cstics[csticKey]),
      };

      valid = GUIDEWAY_PART_LENGTH_VALIDATOR(state, validationObj, false);
      const extraParamName = getExtraValidationParamName({
        index: kmatIndex,
        type: kmatType,
        key: csticKey,
      });
      extraParameters[extraParamName] = valid;

      index++;
    }
    // Validate ALAR
    for (const csticKey of [CSTIC_AL_NAME, CSTIC_AR_NAME]) {
      const validationObj = {
        index: kmatIndex,
        type: kmatType,
        key: csticKey,
        value: getCsticVal(kmat.cstics[csticKey]),
      };
      const valid = GUIDEWAY_EXTENDED_ALAR_VALIDATOR(state, validationObj, ignoreEmpty);
      const extraParamName = getExtraValidationParamName(validationObj);
      extraParameters[extraParamName] = valid;
    }
  });
  return extraParameters;
};

const updateCarriageValidations = (state: LinearConfiguratorState, ignoreEmpty = false) => {
  const extraParameters: Dict<boolean> = {};
  const kmatType = selectDeepCarriageKmatType(state);
  state.kmats[kmatType].forEach((kmat, kmatIndex) => {
    getEditCarriageCsticsToReset(state.type!)
      .filter((key) => key !== CSTIC_VSKITS_NAME)
      .forEach((csticKey) => {
        if (kmat.cstics[csticKey] !== null && kmat.cstics[csticKey] !== undefined) {
          const validationObj = {
            index: kmatIndex,
            type: kmatType,
            key: csticKey,
            value: getCsticVal(kmat.cstics[csticKey]),
          };
          const position = getCsticVal(kmat.cstics[CSTIC_SCHMIERANSCHLUSS_NAME]);
          let valid;
          if (
            csticKey === CSTIC_SCHMIERANSCHLUSS_POSITION_NAME &&
            (position === UNMOUNTED_LEFT || position === UNMOUNTED_RIGHT)
          ) {
            // Don't validate position if it's already mounted
            valid = true;
          } else {
            valid = EDIT_CARRIAGE_VALUE_VALIDATOR(state, validationObj, false);
          }
          const extraParamName = getExtraValidationParamName(validationObj);
          extraParameters[extraParamName] = valid;
        }
      });
  });
  return extraParameters;
};

type CsticValidator = (state: LinearConfiguratorState, update: CsticUpdate, ignoreEmpty: boolean) => boolean;

const GUIDEWAY_EXTENDED_ALAR_VALIDATOR: CsticValidator = (state, update, ignoreEmpty) => {
  if (!update.value) {
    return ignoreEmpty;
  }
  let valid = true;
  const value = +update.value;

  const minSize = 0;
  valid = valid && value > minSize;

  const kmatType = selectDeepRailKmatType(state);

  const maxSize = getCsticNumberVal(state.kmats[kmatType][update.index!].cstics[AlarHardMax]);
  if (maxSize !== null) {
    valid = valid && value <= maxSize;
  }
  return valid;
};

const GUIDEWAY_LENGTH_VALIDATOR: CsticValidator = (state, update, ignoreEmpty) => {
  if (!update.value) {
    return ignoreEmpty;
  }
  let valid = true;
  if (state.minLength && +update.value < state.minLength) {
    valid = false;
  }
  if (state.maxLength && +update.value > state.maxLength) {
    valid = false;
  }
  return valid;
};

const GUIDEWAY_PART_LENGTH_VALIDATOR: CsticValidator = (state, update, ignoreEmpty) => {
  if (!update.value) {
    return ignoreEmpty;
  }
  const kmatType = selectDeepRailKmatType(state);
  const cstics = state.kmats[kmatType][update.index!].cstics;

  let valid = true;

  const minPartLength = getCsticNumberVal(cstics[CSTIC_PART_MIN_SIZE_NAME]);
  if (minPartLength && +update.value < minPartLength) {
    valid = false;
  }

  const maxPartLength = getCsticNumberVal(cstics[CSTIC_PART_MAX_SIZE_NAME]);
  if (maxPartLength && +update.value > maxPartLength) {
    valid = false;
  }
  return valid;
};

const EDIT_CARRIAGE_VALUE_VALIDATOR: CsticValidator = (state, update, ignoreEmpty) => {
  return update && update.value != null;
};

function constructUpdatedKmats(kmats: Record<KmatType, Kmat[]>, dataToUpdate: CsticUpdate[]): Record<KmatType, Kmat[]> {
  const updatedKmats = JSON.parse(JSON.stringify(kmats)) as Record<KmatType, Kmat[]>;
  dataToUpdate.forEach(({ key, type, index, value }) => {
    const kmatList = updatedKmats[type];
    if (kmatList) {
      kmatList.forEach((kmat, kmatIndex) => {
        if (index === undefined || index === kmatIndex) {
          const cstic = kmat.cstics[key];
          if (cstic) {
            kmat.cstics[key] = {
              ...cstic,
              value: value === null ? undefined : value,
            };
          }
        }
      });
    }
  });
  return updatedKmats;
}

export function isLengthValid(minLength: number | null, maxLength: number | null, length: number) {
  let valid = true;
  if (minLength !== null) {
    valid = valid && length >= minLength;
  }
  if (maxLength !== null) {
    valid = valid && length <= maxLength;
  }
  return valid;
}

function activateRailsSummaryAfterStep4LengthFilled(state: LinearConfiguratorState) {
  if (state.type !== ConfiguratorType.Rail || state.productCode !== RAIL_CONFIGURATOR_PRODUCT_CODE) {
    return;
  }
  // hacky solution for inconsistent with other configurators requirement https://agile.deloitte.de/browse/SCHAEFF-18157
  // other configurators do not allow to enable stepper step in the middle of other step
  const validationObj = {
    index: 1,
    type: KmatType.Main,
    key: CSTIC_LENGTH_NAME,
    value: getCsticVal(state.kmats[KmatType.Main][0].cstics[CSTIC_LENGTH_NAME]),
  };
  const isLengthValid = GUIDEWAY_LENGTH_VALIDATOR(state, validationObj, false);
  const isStep4Active = state.steps[3].active;
  if (isStep4Active && isLengthValid) {
    state.steps[4].active = true;
  }
}

function validateStepFromConflicts(substep: LinearSubstep, conflictedCstics: string[]): void {
  const inputCstics = (Array.isArray(substep.cstics) ? substep.cstics[0] : substep.cstics).input;
  if (inputCstics) {
    Object.values(inputCstics)
      .filter((cstic) => cstic.required && cstic.visible)
      .forEach((cstic) => {
        if (conflictedCstics.includes(cstic.name)) {
          substep.isInvalid = true;
          substep.isLenientInvalid = true;
          // TODO - type?
          const key = getExtraValidationParamName(SimpleCsticId(cstic.name));
          substep.additionalValidations[key] = false;
        }
      });
  }
}
